import React, { useState } from 'react';


const PatternAnalysis = ({ data, days }) => {
  const [viewMode, setViewMode] = useState('revenue'); // revenue, clients, or both

  if (!data || !data.day_patterns || data.day_patterns.length === 0) {
    return (
      <div className="pattern-analysis-container">
        <div className="loading-state">No pattern data available for the selected period.</div>
      </div>
    );
  }

  const { day_patterns, strongest_day, weakest_day, opportunity } = data;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format number
  const formatNumber = (value, decimals = 2) => {
    return value.toFixed(decimals);
  };

  // Get max value for scaling bars
  const maxRevenue = Math.max(...day_patterns.map(d => d.revenue_per_hour));
  const maxClients = Math.max(...day_patterns.map(d => d.clients_per_hour));

  // Get bar color based on performance relative to best day
  const getBarColor = (value, maxValue) => {
    const percentage = (value / maxValue) * 100;
    if (percentage >= 90) return '#10b981'; // Green - excellent
    if (percentage >= 75) return '#3b82f6'; // Blue - good
    if (percentage >= 60) return '#f59e0b'; // Amber - moderate
    return '#ef4444'; // Red - weak
  };

  // Day name mapping
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="pattern-analysis-container">
      {/* Header */}
      <div className="pattern-header">
        <h2>Day-of-Week Performance Patterns</h2>
        <p className="pattern-subtitle">
          Identify your strongest and weakest days to optimise scheduling and marketing
        </p>
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={`toggle-button ${viewMode === 'revenue' ? 'active' : ''}`}
          onClick={() => setViewMode('revenue')}
        >
          Revenue per Hour
        </button>
        <button 
          className={`toggle-button ${viewMode === 'clients' ? 'active' : ''}`}
          onClick={() => setViewMode('clients')}
        >
          Clients per Hour
        </button>
        <button 
          className={`toggle-button ${viewMode === 'both' ? 'active' : ''}`}
          onClick={() => setViewMode('both')}
        >
          Both Metrics
        </button>
      </div>

      {/* Key Insights Banner */}
      <div className="pattern-insights-banner">
        <div className="insight-item">
          <span className="insight-label">Strongest Day:</span>
          <span className="insight-value strongest">{dayNames[strongest_day.day_of_week]}</span>
          <span className="insight-detail">
            {formatCurrency(strongest_day.revenue_per_hour)}/hr
          </span>
        </div>
        <div className="insight-item">
          <span className="insight-label">Weakest Day:</span>
          <span className="insight-value weakest">{dayNames[weakest_day.day_of_week]}</span>
          <span className="insight-detail">
            {formatCurrency(weakest_day.revenue_per_hour)}/hr
          </span>
        </div>
        <div className="insight-item">
          <span className="insight-label">Performance Gap:</span>
          <span className="insight-value gap">
            {formatCurrency(strongest_day.revenue_per_hour - weakest_day.revenue_per_hour)}/hr
          </span>
          <span className="insight-detail">
            {((strongest_day.revenue_per_hour - weakest_day.revenue_per_hour) / weakest_day.revenue_per_hour * 100).toFixed(0)}% difference
          </span>
        </div>
        <div className="insight-item">
          <span className="insight-label">Annual Opportunity:</span>
          <span className="insight-value opportunity">{formatCurrency(opportunity)}</span>
          <span className="insight-detail">
            From improving weak days
          </span>
        </div>
      </div>

      {/* Chart Area */}
      <div className="pattern-chart-container">
        {(viewMode === 'revenue' || viewMode === 'both') && (
          <div className="chart-section">
            <h3>Revenue per Hour by Day</h3>
            <div className="bar-chart">
              {day_patterns.map((day) => (
                <div key={day.day_of_week} className="bar-group">
                  <div className="bar-container">
                    <div 
                      className="bar revenue-bar"
                      style={{
                        height: `${(day.revenue_per_hour / maxRevenue) * 100}%`,
                        backgroundColor: getBarColor(day.revenue_per_hour, maxRevenue)
                      }}
                    >
                      <span className="bar-value">{formatCurrency(day.revenue_per_hour)}</span>
                    </div>
                  </div>
                  <span className="bar-label">{dayNames[day.day_of_week].substring(0, 3)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(viewMode === 'clients' || viewMode === 'both') && (
          <div className="chart-section">
            <h3>Clients per Hour by Day</h3>
            <div className="bar-chart">
              {day_patterns.map((day) => (
                <div key={day.day_of_week} className="bar-group">
                  <div className="bar-container">
                    <div 
                      className="bar clients-bar"
                      style={{
                        height: `${(day.clients_per_hour / maxClients) * 100}%`,
                        backgroundColor: getBarColor(day.clients_per_hour, maxClients)
                      }}
                    >
                      <span className="bar-value">{formatNumber(day.clients_per_hour)}</span>
                    </div>
                  </div>
                  <span className="bar-label">{dayNames[day.day_of_week].substring(0, 3)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detailed Day Breakdown */}
      <div className="day-breakdown-section">
        <h3>Detailed Day Analysis</h3>
        <div className="day-cards-grid">
          {day_patterns.map((day) => {
            const isStrongest = day.day_of_week === strongest_day.day_of_week;
            const isWeakest = day.day_of_week === weakest_day.day_of_week;
            const performanceLevel = 
              day.revenue_per_hour >= maxRevenue * 0.9 ? 'excellent' :
              day.revenue_per_hour >= maxRevenue * 0.75 ? 'good' :
              day.revenue_per_hour >= maxRevenue * 0.6 ? 'moderate' : 'weak';

            return (
              <div 
                key={day.day_of_week} 
                className={`day-card ${performanceLevel} ${isStrongest ? 'strongest' : ''} ${isWeakest ? 'weakest' : ''}`}
              >
                <div className="day-card-header">
                  <h4>{dayNames[day.day_of_week]}</h4>
                  {isStrongest && <span className="badge-strongest">🌟 Strongest</span>}
                  {isWeakest && <span className="badge-weakest">⚠️ Weakest</span>}
                </div>
                
                <div className="day-metrics">
                  <div className="day-metric">
                    <span className="day-metric-label">Revenue/Hour:</span>
                    <span className="day-metric-value">{formatCurrency(day.revenue_per_hour)}</span>
                  </div>
                  <div className="day-metric">
                    <span className="day-metric-label">Clients/Hour:</span>
                    <span className="day-metric-value">{formatNumber(day.clients_per_hour)}</span>
                  </div>
                  <div className="day-metric">
                    <span className="day-metric-label">Avg Ticket:</span>
                    <span className="day-metric-value">{formatCurrency(day.avg_ticket)}</span>
                  </div>
                  <div className="day-metric">
                    <span className="day-metric-label">Total Hours:</span>
                    <span className="day-metric-value">{formatNumber(day.total_hours, 1)}</span>
                  </div>
                </div>

                {day.gap_to_strongest > 0 && (
                  <div className="day-opportunity">
                    <span className="opportunity-label">Gap to best day:</span>
                    <span className="opportunity-value">
                      {formatCurrency(day.gap_to_strongest)}/hr
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Recommendations */}
      <div className="pattern-recommendations">
        <h3>Action Recommendations</h3>
        <div className="recommendations-grid">
          {/* For weak days */}
          {day_patterns
            .filter(d => d.revenue_per_hour < maxRevenue * 0.75)
            .map(day => (
              <div key={`rec-${day.day_of_week}`} className="recommendation-card">
                <div className="recommendation-header">
                  <span className="recommendation-day">{dayNames[day.day_of_week]}</span>
                  <span className="recommendation-badge">Improvement Needed</span>
                </div>
                <div className="recommendation-content">
                  <p className="recommendation-issue">
                    Currently {formatCurrency(day.revenue_per_hour)}/hr 
                    ({((maxRevenue - day.revenue_per_hour) / maxRevenue * 100).toFixed(0)}% below peak)
                  </p>
                  <ul className="recommendation-actions">
                    {day.clients_per_hour < maxClients * 0.8 && (
                      <li>📅 Run targeted promotions to fill {dayNames[day.day_of_week]} slots</li>
                    )}
                    {day.avg_ticket < strongest_day.avg_ticket * 0.9 && (
                      <li>💰 Focus on upselling higher-value services on {dayNames[day.day_of_week]}</li>
                    )}
                    <li>📢 Social media campaign highlighting {dayNames[day.day_of_week]} availability</li>
                    <li>🎁 Consider {dayNames[day.day_of_week]}-specific loyalty rewards</li>
                    {day.total_hours < strongest_day.total_hours * 0.8 && (
                      <li>👥 Increase staffing to capture more demand</li>
                    )}
                  </ul>
                  <p className="recommendation-potential">
                    <strong>Potential gain:</strong> If brought to 80% of best day = 
                    {formatCurrency((maxRevenue * 0.8 - day.revenue_per_hour) * day.total_hours * (365 / days))} annually
                  </p>
                </div>
              </div>
            ))}

          {/* For strong days */}
          {day_patterns
            .filter(d => d.revenue_per_hour >= maxRevenue * 0.9)
            .map(day => (
              <div key={`rec-${day.day_of_week}`} className="recommendation-card success">
                <div className="recommendation-header">
                  <span className="recommendation-day">{dayNames[day.day_of_week]}</span>
                  <span className="recommendation-badge success">Optimise Further</span>
                </div>
                <div className="recommendation-content">
                  <p className="recommendation-issue">
                    Strong performance at {formatCurrency(day.revenue_per_hour)}/hr
                  </p>
                  <ul className="recommendation-actions">
                    <li>✅ This is your template for success - analyse what works</li>
                    <li>📊 Review {dayNames[day.day_of_week]} client demographics for targeting</li>
                    <li>💎 Consider premium service offerings on high-demand {dayNames[day.day_of_week]}s</li>
                    <li>🔄 Ensure you're not turning away clients - could you add capacity?</li>
                  </ul>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Summary Calculation */}
      <div className="pattern-summary">
        <h3>The Opportunity</h3>
        <div className="summary-content">
          <p>
            If you could bring your weakest days up to just <strong>80% of your best day's performance</strong>, 
            you would generate an additional <strong>{formatCurrency(opportunity)}</strong> annually.
          </p>
          <p>
            This doesn't require new clients or higher prices - just <strong>better utilisation of existing capacity</strong> 
            through targeted marketing and scheduling optimization.
          </p>
        </div>
      </div>

      {/* Data Note */}
      <div className="data-note">
        <p>
          <strong>Methodology:</strong> Day-of-week patterns calculated from {days} days of actual data. 
          Opportunity assumes bringing underperforming days to 80% of strongest day's revenue per hour, 
          accounting for current staffing levels. Annual projections based on 52-week year.
        </p>
      </div>
    </div>
  );
};

export default PatternAnalysis;